import { useCallback, useMemo } from 'react'

import type { Logger } from '@vp/ubik-logging'

import { useCartIdentifiers } from './useCartIdentifiers'

import { createCcv2CartApi } from '../clients/CartApi'
import type { WorkId } from '../types/WorkId'

export type IUseCart = {
  upsertCartAsync: (workId: WorkId, culture: string, authorizationHeader: string | undefined, logger: Logger) => Promise<void>
  upsertCartMultiWorkAsync: (workIds: string[], culture: string, authorizationHeader: string | undefined, logger: Logger) => Promise<void>
  verifyCartContainsWorkIds: (workIds: string[], culture: string, authorizationHeader: string | undefined, logger: Logger) => Promise<boolean>
}

export const useCart = () => {
  const { shopperId, accountId, cartTypeId, cartManagerTenant } = useCartIdentifiers()

  const cartApi = useMemo(() => createCcv2CartApi({ accountId, cartTypeId, cartManagerTenant }), [accountId, cartTypeId, cartManagerTenant])

  const upsertCartAsync = useCallback(
    async (workId: WorkId, culture: string, authorizationHeader: string | undefined, logger: Logger): Promise<void> => {
      if (!authorizationHeader) {
        const error = new Error('Cannot add to cart: user not authenticated')
        logger.error('Cart upsert failed - missing authorization', error, { workId, culture })
        throw error
      }
      await cartApi.upsertWork(workId, culture, authorizationHeader, logger)
    },
    [cartApi],
  )

  const upsertCartMultiWorkAsync = useCallback(
    async (workIds: WorkId[], culture: string, authorizationHeader: string | undefined, logger: Logger): Promise<void> => {
      if (!authorizationHeader) {
        const error = new Error('Cannot add to cart: user not authenticated')
        logger.error('Cart multi-work upsert failed - missing authorization', error, { workIds, culture })
        throw error
      }
      await cartApi.upsertWorks(workIds, culture, authorizationHeader, logger)
    },
    [cartApi],
  )

  const verifyCartContainsWorkIds = useCallback(
    async (workIds: string[], culture: string, authorizationHeader: string | undefined, logger: Logger): Promise<boolean> => {
      if (!authorizationHeader) {
        const error = new Error('Cannot verify work is in cart: user not authenticated')
        logger.error('Cart verification failed - missing authorization', error, {
          workIds,
          culture,
        })
        return false
      }

      if (!shopperId) {
        const error = new Error('Cannot verify work is in cart: missing shopperId')
        logger.error('Cart verification failed - missing shopperId', error, { workIds, culture })
        return false
      }

      try {
        const { workIds: cartWorkIdsList } = await cartApi.getCart(shopperId, authorizationHeader, logger)

        const cartWorkIds = new Set<string>(cartWorkIdsList)

        const foundWorkIds = workIds.filter((workId) => cartWorkIds.has(workId))
        const allFound = foundWorkIds.length === workIds.length

        return allFound
      } catch (error) {
        logger.error('Cart verification failed - error fetching cart', error as Error, { workIds, culture })
        return false
      }
    },
    [cartApi, shopperId],
  )

  return {
    upsertCartAsync,
    upsertCartMultiWorkAsync,
    verifyCartContainsWorkIds,
  }
}
